import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { X, Plus, Minus, ShoppingCart, Trash2, ArrowRight, Zap, Truck } from 'lucide-react';
import { categories, FREE_SHIPPING_THRESHOLD } from '../../data/mock';

const CartDrawer = () => {
  const { 
    isCartOpen, 
    closeCart, 
    cartItems, 
    cartTotal, 
    updateQuantity, 
    removeFromCart 
  } = useCart();

  if (!isCartOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
        onClick={closeCart}
      />
      
      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-zinc-950 z-50 shadow-2xl flex flex-col border-l border-white/10">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-lime-400 rounded-xl flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-black" />
            </div>
            <div>
              <h2 className="text-lg text-white font-bold">Your Cart</h2>
              <p className="text-sm text-white/50">{cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}</p>
            </div>
          </div>
          <button 
            onClick={closeCart}
            className="p-2 hover:bg-white/10 rounded-xl transition-colors text-white/60 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-20 h-20 bg-white/5 rounded-2xl flex items-center justify-center mb-5 border border-white/10">
                <Zap className="w-8 h-8 text-white/20" />
              </div>
              <h3 className="font-bold text-white text-lg mb-2">Your cart is empty</h3>
              <p className="text-white/50 text-sm mb-6">Add some gear to get started</p>
              <button 
                onClick={closeCart}
                className="px-6 py-3 bg-lime-400 hover:bg-lime-300 text-black font-bold uppercase tracking-wider rounded-xl transition-all"
              >
                Browse Products
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map(item => {
                const itemCategory = categories.find(c => c.id === item.categoryId);
                return (
                  <div key={item.id} className="flex gap-4 p-4 bg-white/5 rounded-2xl border border-white/10">
                    {/* Image */}
                    <div className="w-20 h-20 bg-black rounded-xl overflow-hidden flex-shrink-0">
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-lime-400 uppercase tracking-wider font-semibold mb-1">
                        {itemCategory?.name || 'Fitness Gear'}
                      </p>
                      <h4 className="font-medium text-white text-sm mb-1 line-clamp-2">
                        {item.name}
                      </h4>
                      <p className="text-white font-bold">
                        ${item.price.toFixed(2)}
                      </p>
                      
                      {/* Quantity Controls */}
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center border border-white/20 rounded-lg overflow-hidden">
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="p-2 hover:bg-white/10 transition-colors text-white/60"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-8 text-center text-sm font-semibold text-white">
                            {item.quantity}
                          </span>
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="p-2 hover:bg-white/10 transition-colors text-white/60"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <button 
                          onClick={() => removeFromCart(item.id)}
                          className="p-2 text-white/40 hover:text-red-400 hover:bg-white/5 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="p-6 border-t border-white/10 space-y-4">
            {/* Free Shipping Progress */}
            {cartTotal < FREE_SHIPPING_THRESHOLD && (
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-white/60 flex items-center gap-2">
                    <Truck className="w-4 h-4 text-lime-400" />
                    ${(FREE_SHIPPING_THRESHOLD - cartTotal).toFixed(2)} more for FREE shipping
                  </span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-1.5 overflow-hidden">
                  <div 
                    className="h-full bg-lime-400 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min((cartTotal / FREE_SHIPPING_THRESHOLD) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}
            {cartTotal >= FREE_SHIPPING_THRESHOLD && (
              <div className="flex items-center gap-2 px-4 py-3 bg-lime-400/10 border border-lime-400/30 rounded-xl">
                <Truck className="w-5 h-5 text-lime-400" />
                <span className="text-lime-400 text-sm font-medium">FREE shipping unlocked!</span>
              </div>
            )}

            {/* Subtotal */}
            <div className="flex items-center justify-between pt-2">
              <span className="text-white/60 font-medium">Subtotal</span>
              <span className="text-2xl font-black text-white">${cartTotal.toFixed(2)}</span>
            </div>

            {/* Checkout Button */}
            <Link
              to="/checkout"
              onClick={closeCart}
              className="flex items-center justify-center gap-2 w-full py-4 bg-lime-400 hover:bg-lime-300 text-black font-bold uppercase tracking-wider rounded-xl transition-all"
            >
              Checkout
              <ArrowRight className="w-5 h-5" />
            </Link>

            {/* Continue Shopping */}
            <button 
              onClick={closeCart}
              className="w-full text-center text-white/50 hover:text-white py-2 text-sm font-medium transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
